import { Pencil, Play, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type Meeting } from "@/lib/api";
import { formatDuration } from "@/lib/format";
import { StatusPill } from "@/pages/Recordings";

function NotAMeetingBadge() {
  return (
    <span className="rounded-[var(--radius-full)] border border-[var(--border-default)] px-2 py-0.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)]">
      Not a meeting
    </span>
  );
}

/**
 * Step 2's content: the recording's meetings (recording/meeting split) — one exists the
 * moment the audio phase finishes, spanning the whole recording, and its page's "Split
 * here" (offered until its content phase runs) can carve into more. Splitting, naming and
 * describing are all here; so is sending each one into its own content-phase run, which
 * is the step the wizard exists to lead up to.
 */
export function MeetingsPhase({
  meetings,
  onChanged,
  awaitingProposal,
}: {
  meetings: Meeting[] | null;
  onChanged: (updated: Meeting) => void;
  /** The recording's proposal has not been adopted, so its one meeting is only the whole
   *  recording held in place — not something to process (the Server refuses it too). */
  awaitingProposal: boolean;
}) {
  const { api } = useApp();
  const [startingAll, setStartingAll] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (meetings === null) {
    return (
      <p className="px-[var(--space-2)] py-[var(--space-6)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Loading…
      </p>
    );
  }

  if (awaitingProposal) {
    return (
      <p className="rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] px-[var(--space-6)] py-[var(--space-8)] text-center text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Approve the proposed meetings above first. The meetings you create appear here, ready to process one by one
        or all at once.
      </p>
    );
  }

  if (meetings.length === 0) {
    return (
      <p className="rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] px-[var(--space-6)] py-[var(--space-8)] text-center text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        No meeting has been carved out of this recording yet.
      </p>
    );
  }

  // Worth a bulk action once there is more than one meeting waiting — a single meeting
  // already has this exact button on its own card, and a second one next to it would
  // just be the same click offered twice.
  const notStarted = meetings.filter((meeting) => meeting.status === null && !meeting.notAMeeting);

  async function startAll() {
    setStartingAll(true);
    setProblem(null);
    try {
      await Promise.all(
        notStarted.map(async (meeting) => {
          const started = await api.startPipeline(meeting.id);
          onChanged({ ...meeting, status: started.status });
        }),
      );
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "Not every meeting could be started.");
    } finally {
      setStartingAll(false);
    }
  }

  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      {notStarted.length > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-dashed border-[var(--border-default)] px-[var(--space-4)] py-[var(--space-3)]">
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            {notStarted.length} meetings ready to process.
          </p>
          <button
            type="button"
            onClick={() => void startAll()}
            disabled={startingAll}
            className="flex shrink-0 items-center gap-[var(--space-2)] rounded-[var(--radius-lg)] bg-[var(--primary)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
          >
            <Play size={14} strokeWidth={2} />
            {startingAll ? "Starting…" : `Start all ${notStarted.length}`}
          </button>
        </div>
      )}

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <ul className="flex flex-col gap-[var(--space-3)]">
        {meetings.map((meeting) => (
          <MeetingCard key={meeting.id} meeting={meeting} onChanged={onChanged} />
        ))}
      </ul>
    </div>
  );
}

function MeetingCard({ meeting, onChanged }: { meeting: Meeting; onChanged: (updated: Meeting) => void }) {
  const { api } = useApp();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(meeting.title ?? "");
  const [description, setDescription] = useState(meeting.description ?? "");
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // The same action PipelinePanel offers on the meeting's own page — here too, because
  // that page is a click away and this is the point where a reader has just finished
  // describing the meeting and is looking for what comes next.
  async function start() {
    setStarting(true);
    setProblem(null);
    try {
      const started = await api.startPipeline(meeting.id);
      onChanged({ ...meeting, status: started.status });
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be started.");
    } finally {
      setStarting(false);
    }
  }

  return (
    <li className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-5)]">
      {editing ? (
        <div className="flex flex-col gap-[var(--space-2)]">
          <input
            autoFocus
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Meeting name"
            className="h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-3 text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]"
          />
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Description (optional)"
            rows={2}
            className="resize-none rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-3 py-2 text-[length:var(--text-sm)] text-[var(--text-primary)]"
          />
          <div className="flex items-center gap-[var(--space-3)]">
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const updated = await api.updateMeeting(meeting.id, { title, description });
                  onChanged(updated);
                  setEditing(false);
                } finally {
                  setBusy(false);
                }
              }}
              className="inline-flex h-8 items-center rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing(false)}
              className="text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          <div className="min-w-0">
            <Link
              to={`/recordings/${meeting.id}`}
              className="text-[length:var(--text-md)] font-semibold text-[var(--text-primary)] hover:underline"
            >
              {meeting.title ?? "Untitled meeting"}
            </Link>
            {meeting.description && (
              <p className="mt-1 max-w-[60ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
                {meeting.description}
              </p>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-[var(--space-3)]">
              {formatDuration(meeting.durationMs) && (
                <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
                  {formatDuration(meeting.durationMs)}
                </span>
              )}
              {meeting.notAMeeting ? <NotAMeetingBadge /> : <StatusPill status={meeting.status} />}
            </div>

            {problem && (
              <p role="alert" className="mt-1 text-[length:var(--text-xs)] text-[var(--warning)]">
                {problem}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-[var(--space-2)]">
            {/* Review before starting is the point of this step, so it is one click from
                here rather than behind the meeting's own page — but secondary, because
                starting is still what the step leads to. */}
            <Link
              to={`/recordings/${meeting.id}/review`}
              state={{
                splittable: meeting.status === null || meeting.status === "failed" || meeting.status === "cancelled",
              }}
              className="flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
            >
              Open review mode
            </Link>

            {!meeting.notAMeeting &&
              (meeting.status === null || meeting.status === "failed" || meeting.status === "cancelled") && (
              <button
                type="button"
                onClick={() => void start()}
                disabled={starting}
                className="flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
              >
                {meeting.status === null ? (
                  <Play size={13} strokeWidth={2} />
                ) : (
                  <RotateCcw size={13} strokeWidth={2} />
                )}
                {starting ? "Starting…" : meeting.status === null ? "Start processing" : "Run it again"}
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setTitle(meeting.title ?? "");
                setDescription(meeting.description ?? "");
                setEditing(true);
              }}
              aria-label="Rename or describe this meeting"
              className="shrink-0 text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
            >
              <Pencil size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
